import { FC, useEffect, useState } from 'react'
import { Categories } from './Categories'
import { AllergensModal } from './AllergensModal'
import { useSearchParams } from 'react-router-dom'
import { CategoriesSlider } from './CategoriesSlider'
import { useOpenModal } from '../../featureSliced/shared/hooks/useOpenModal'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { FiltersSlice } from 'featureSliced/shared/store/reducers/FiltersReducer'
import { useMyDispatch } from '../../featureSliced/shared/hooks/useMyDispatch'
import { Scanner } from '../../components/Scanner'
import { Header } from '../../components/Header'
import { toast } from 'react-toastify'
import { Footer } from './Footer'

import {
  ICategory,
  IProduct,
} from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { QrMenuStyledContainer } from './styled'
import { handleLocalStorage } from '../../featureSliced/shared/utils/handleLocalStorage'
import { usePromptParams } from '../../featureSliced/shared/hooks/usePromptParams'
import { useTranslations } from '../../featureSliced/shared/hooks/useTranslations'
import TableSelect from 'featureSliced/features/TableSelect'
import { loadTableStatus } from 'featureSliced/shared/store/reducers/QRMenuTableReducer/actionCreator'
import { TableStatuses } from 'featureSliced/shared/store/reducers/QRMenuTableReducer/types'
import { ToastsSlice } from 'featureSliced/shared/store/reducers/ToasterReducer'
import { useGetTranslatedNames } from 'featureSliced/shared/hooks/useGetTranslatedNames'
import { SpinnerOverlay } from 'components/OverlaySpinner'
import { useOpenProduct } from 'featureSliced/shared/hooks/openModals/useOpenProductModal'
import moment from 'moment'
import { findCatalogProduct } from 'featureSliced/shared/utils/findCatalogProduct'
import {
  getItemLeadDays,
  isItemLeadDaysExceeded,
} from 'featureSliced/shared/utils/itemLeadDays'
import { isPickupTimeAllowed } from 'featureSliced/shared/utils/getEarliestPickup'
import { QRMBucketSlice } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer'

type MenuProps = {
  loadMenuData: () => Promise<void>
}

export const Menu: FC<MenuProps> = ({ loadMenuData }) => {
  // Hooks
  const dispatch = useMyDispatch()
  const openModal = useOpenModal()
  const t = useTranslations()
  const openProductModalQ = useOpenProduct()
  const { getProductName } = useGetTranslatedNames()
  const [searchParams] = useSearchParams()
  const openPromptModal = usePromptParams()
  const payment_success_nr = searchParams.get('payment_success_nr')

  // Actions
  const { discardAllergens, discardDiet } = FiltersSlice.actions

  // Store
  const isProductsLoaded = useMySelector((state) => state.app.isProductsLoaded)
  const isSpecialsLoaded = useMySelector(
    (state) => state.specials.isSpecialsLoaded
  )
  const isProductsLoading = useMySelector((state) => state.categories.isLoading)
  const { allergens, diet, search } = useMySelector((state) => state.filters)
  const categoriesFromState = useMySelector((state) => state.categories.items)
  const bucketProducts = useMySelector((state) => state.bucket.products)
  const { leadTimeDays, leadTimeHours } = useMySelector(
    (state) => state.app.clickCollectConfig
  )
  const { bookingVariant, disabledByMobileWaiter } = useMySelector(
    (state) => state.app.menu
  )
  const { isTableDataLoaded, isTableLoading, tableStatus } = useMySelector(
    (state) => state.qrMenuTable
  )
  const openToast = ToastsSlice.actions.openToast
  const removeProductFromBucket = QRMBucketSlice.actions.removeProductFromBucket

  // State
  const [categories, setCategories] = useState<ICategory[]>([])
  const [isInputShowing, setIsInputShowing] = useState<boolean>(false)
  const [shouldFilterCategories, setShouldFilterCategories] =
    useState<boolean>(false)

  const { registerId, branchId, dataEnc, pickUpTime } = handleLocalStorage([
    'registerId',
    'branchId',
    'dataEnc',
    'pickUpTime',
  ])

  // useEffects
  useEffect(() => {
    if (
      isTableDataLoaded &&
      !isTableLoading &&
      tableStatus === TableStatuses.BUSY
    ) {
      dispatch(
        openToast({
          message: t('errors.tableIsLocked'),
          type: 'error',
        })
      )
    }
  }, [isTableDataLoaded, isTableLoading, tableStatus])

  useEffect(() => {
    promptTableId()
  }, [isProductsLoaded, bookingVariant])

  useEffect(() => {
    // TODO: check if pickUpTime works correctly
    const isClick = process.env.REACT_APP_IS_CLIC === '1'
    // A pickUpTime left over from a previous visit (customer returns days later, or the
    // wizard was skipped by a stale bundle) is treated the same as no pickUpTime at all —
    // otherwise the menu loads with a past pickup date and every lead-time item shows as
    // exceeded with no way to fix it (QRM-639). The same applies when the stored time no
    // longer satisfies the company's lead-days/cutoff rule (the shop raised the setting
    // since the last visit) — force a fresh pick instead of letting an unorderable date
    // ride into checkout.
    const isPickUpTimeStale =
      isClick &&
      !!pickUpTime &&
      !isPickupTimeAllowed({
        now: moment(),
        pickUpTime,
        leadTimeDays,
        cutoff: leadTimeHours,
      })
    if (isClick && (!pickUpTime || isPickUpTimeStale)) {
      openPromptModal(loadMenuData, ['time', 'branch'], true)
    } else if (isClick && pickUpTime && !branchId) {
      openPromptModal(loadMenuData, ['branch'], true)
    } else {
      loadMenuData()
    }
  }, [payment_success_nr])

  // C&C: a product's per-item lead time can stop being met between "added to cart" and
  // "back to check out" (cart built yesterday, or the shop changed the setting) — drop
  // those lines the moment the catalogue is available rather than only at checkout, so the
  // cart the customer sees is always the cart they can actually order (QRM-639).
  useEffect(() => {
    if (process.env.REACT_APP_IS_CLIC !== '1') return
    if (
      !isProductsLoaded ||
      !bucketProducts?.length ||
      !categoriesFromState?.length
    )
      return
    const flatProducts = categoriesFromState.flatMap((cat) => cat.products)
    const exceededLines = bucketProducts.filter((line) => {
      const catalogProduct = findCatalogProduct(flatProducts, line.productId)
      if (!catalogProduct) return false
      const leadDays = getItemLeadDays(catalogProduct.ClickCollectLeadDaysCount)
      return isItemLeadDaysExceeded({ leadDays, pickUpTime })
    })
    if (exceededLines.length) {
      exceededLines.forEach((line) =>
        dispatch(removeProductFromBucket(line.uniqueId))
      )
      dispatch(
        openToast({
          message: t('clickCollect.leadDaysItemsRemoved'),
          type: 'error',
        })
      )
    }
  }, [isProductsLoaded, categoriesFromState]) // eslint-disable-line

  useEffect(() => {
    if (!allergens.length && !diet.length && !search?.length) {
      setCategories(categoriesFromState)
    } else {
      const categoriesToSet = categoriesFromState?.map((cat) => {
        let products = [...cat.products]
        if (allergens.length) {
          products = products?.filter(
            (product1) =>
              !product1.allergens?.some((al) => allergens?.includes(al.id))
          )
        }
        if (diet.length) {
          products = products?.filter((product2) =>
            product2.dietItems?.every((d) => diet?.includes(d.code))
          )
        }

        if (search?.length) {
          products = products?.filter(
            (product3) =>
              // !!product3?.title
              !!getProductName(product3)
                ?.toLowerCase()
                ?.includes?.(search?.toLowerCase())
          )
        }
        return { ...cat, products }
      })
      setCategories(categoriesToSet)
    }
  }, [categoriesFromState, shouldFilterCategories, search]) // eslint-disable-line

  // Functions
  const promptTableId = () => {
    const table = searchParams.get('table')
    if (
      !table &&
      isProductsLoaded &&
      process.env.REACT_APP_IS_CLIC === '0' &&
      bookingVariant === 2 &&
      !disabledByMobileWaiter
      // !selfService &&
    ) {
      openModal({
        id: 'TABLE_SELECTION',
        shouldHideCross: true,
        components: [
          ({ closeThisModal }) => (
            <TableSelect
              onClose={() => {
                dispatch(loadTableStatus())
                closeThisModal()
              }}
            />
          ),
        ],
      })
    }
  }

  const openAllergensModal = () => {
    openModal({
      id: 'ALLERGENS_MODAL',
      components: [
        ({ closeThisModal }) => (
          <AllergensModal
            type="menu"
            allergensActions={FiltersSlice.actions}
            onClear={() => {
              dispatch(discardAllergens())
              dispatch(discardDiet())
              setShouldFilterCategories((prevValue) => !prevValue)
              closeThisModal()
            }}
            onApply={() => {
              setShouldFilterCategories((prevValue) => !prevValue)
              closeThisModal()
            }}
          />
        ),
      ],
      title: 'Filters',
    })
  }

  const openProductModal = (product: IProduct) => {
    const divElement = document.getElementById(`product_${product.id}`)
    divElement?.scrollIntoView()
    openProductModalQ(product, product.categoryId)
    // openModal({
    //   id: `ADD_PRODUCT_${product.id}`,
    //   components: [
    //     ({ closeThisModal }) => (
    //       <AddProductModal
    //         product={product}
    //         templateId={product.productTemplateId}
    //         closeProduct={closeThisModal}
    //       />
    //     ),
    //   ],
    //   title: getProductName(product),
    // })
  }

  const openBarcodeModal = () => {
    openModal({
      id: 'BARCODE_SCANNER',
      title: 'Scan',
      components: [
        () => (
          <Scanner
            title={"t('scanner.scanProduct')"}
            subtitle={"t('scanner.hoverBarcode')"}
            onScan={handleBarcodeScan}
          />
        ),
      ],
    })
  }

  const handleBarcodeScan = async (EANCode: string) => {
    const productToOpen = findProductByEANCode(EANCode)
    if (!productToOpen) toast.error(t('scanner.barcodeError'))
    if (productToOpen) {
      openProductModal(productToOpen)
    }
  }

  const findProductByEANCode = (_EANCode: string) => {
    // EANCode is no longer included in the v2 product catalog
    return null
  }

  return (
    <QrMenuStyledContainer>
      {/* <SpinnerOverlay isLoading={!isProductsLoaded} /> */}
      <SpinnerOverlay
        isLoading={
          !isSpecialsLoaded ||
          !isProductsLoaded ||
          isProductsLoading ||
          (process.env.REACT_APP_IS_CLIC === '0' &&
            (!isTableDataLoaded || isTableLoading))
        }
        isTransparent
      />
      <div>
        <Header />
        <CategoriesSlider
          isInputShowing={isInputShowing}
          hideInput={() => setIsInputShowing(false)}
          reloadMenuData={loadMenuData}
          applyCategoriesFilter={() =>
            setShouldFilterCategories((prevValue) => !prevValue)
          }
        />
        <Categories
          categories={categories}
          isInputShowing={isInputShowing}
          isAllergensShowing={!!allergens?.length || !!diet?.length}
        />
        <Footer
          openAllergensModal={openAllergensModal}
          openBarcodeModal={openBarcodeModal}
          setIsInputShowing={() => setIsInputShowing((prevValue) => !prevValue)}
        />
      </div>
    </QrMenuStyledContainer>
  )
}

export default Menu
